import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { RentalTermsDocuments } from './RentalTermsDocuments'
import { Checkbox } from '@/components/ui/Checkbox'
import { Button } from '@/components/ui/Button'
import { useCurrentRentalTerms, useRefreshRentalTerms } from '@/lib/hooks/use-rental-terms'
import { legalService } from '@/lib/services/legal.service'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { captureError } from '@/lib/utils/observability'
import { toast } from '@/lib/toast'

/**
 * A new economic policy never locks a host out of existing obligations. This
 * card is shown on the hosting dashboard for future requests only; declining
 * leaves old bookings and the two-hour decision action available.
 */
export function HostRentalTermsCard() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { data: terms, isLoading, isError, refetch } = useCurrentRentalTerms()
  const refresh = useRefreshRentalTerms()
  const [checked, setChecked] = useState(false)
  const [saving, setSaving] = useState(false)
  const currentNeedsAcceptance = terms?.version !== '1.3' && terms?.accepted === false
  const offered = currentNeedsAcceptance
    ? terms
    : terms?.upcoming?.accepted === false
      ? terms.upcoming
      : null
  const isUpcoming = offered?.version === terms?.upcoming?.version
  useEffect(() => setChecked(false), [offered?.version])

  if (!offered) {
    return isError ? <RentalTermsDocuments terms={null} onRetry={() => void refetch()} /> : null
  }

  const accept = async () => {
    if (!offered || !checked || saving) return
    setSaving(true)
    try {
      if (isUpcoming) await legalService.acceptNoticedRentalTerms(offered.version)
      else await legalService.acceptCurrentRentalTerms(offered.version)
      await refresh()
      toast.success(t('rental_terms_flow.host_accepted'))
    } catch (error) {
      captureError(error, { component: 'HostRentalTermsCard', action: 'accept' })
      setChecked(false)
      void refetch()
      toast.error(t('rental_terms_flow.host_error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <View testID="hosting.terms-update" style={{ gap: SPACING.sm }}>
      <Text style={{ color: colors.textPrimary, fontFamily: FONTS.bodyBold, fontSize: 17 }}>
        {t(isUpcoming ? 'rental_terms_flow.host_upcoming_title' : 'rental_terms_flow.host_title')}
      </Text>
      <Text
        style={{
          color: colors.textSecondary,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 19,
        }}
      >
        {t(isUpcoming ? 'rental_terms_flow.host_upcoming_body' : 'rental_terms_flow.host_body')}
      </Text>
      <RentalTermsDocuments terms={offered} loading={isLoading} onRetry={() => void refetch()}>
        {offered && (
          <>
            <Checkbox
              checked={checked}
              onChange={setChecked}
              label={t('rental_terms_flow.accept_label', { version: offered.version })}
              testID="hosting.terms-update-checkbox"
            />
            <Button
              title={t(
                isUpcoming
                  ? 'rental_terms_flow.host_accept_upcoming'
                  : 'rental_terms_flow.host_accept',
              )}
              variant="primary"
              size="lg"
              disabled={!checked || saving}
              loading={saving}
              onPress={() => void accept()}
              testID="hosting.terms-update-accept"
            />
          </>
        )}
      </RentalTermsDocuments>
    </View>
  )
}
